import { Box } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"
import { useShallow } from "zustand/shallow"

import {
  GoodsReceiptPalletDescriptionCell,
  GoodsReceiptPalletDestinationCell,
  GoodsReceiptPalletProductIdCell,
  GoodsReceiptPalletReferenceIdCell,
  GoodsReceiptPalletTotalUomCell,
  GoodsReceiptPalletUnitsCell,
} from "../cells"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { getUomName } from "@/utils/uom"

export const usePutAwayOrderPalletsColumns = (): ColumnDef<GoodsReceiptPallet>[] => {
  const units = useUnitOfMeasurementsStore(useShallow((s) => s.units))

  const columns = useMemo<ColumnDef<GoodsReceiptPallet>[]>(() => {
    return [
      {
        accessorKey: "referenceId",
        header: "Ref #",
        size: 75,
        cell: ({ row }) => <GoodsReceiptPalletReferenceIdCell pallet={row.original} />,
      },
      {
        accessorKey: "productId",
        header: "Product ID",
        cell: ({ row }) => <GoodsReceiptPalletProductIdCell pallet={row.original} />,
        size: 120,
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ row }) => <GoodsReceiptPalletDescriptionCell pallet={row.original} />,
      },
      {
        accessorKey: "units",
        header: "Units",
        size: 50,
        cell: ({ row }) => <GoodsReceiptPalletUnitsCell pallet={row.original} />,
      },
      {
        accessorKey: "totalUom",
        header: "Total UoM",
        size: 85,
        cell: ({ row }) => (
          <GoodsReceiptPalletTotalUomCell
            pallet={row.original}
            uomName={getUomName(units, row.original.goodsReceiptPalletRow?.product?.packageUsWeightUom)}
          />
        ),
      },
      {
        accessorKey: "location",
        header: "Location",
        enableSorting: false,
        size: 90,
        cell: () => (
          <Box className='flex flex-col'>
            <span>From: I/H</span>
            <span>To: WHS</span>
          </Box>
        ),
      },
      {
        accessorKey: "destinationWarehouseShelving",
        header: "Destination",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletDestinationCell pallet={row.original} shouldShowEdit={false} />,
      },
    ]
  }, [units])

  return columns
}
